// components/create/PhoneVerifySheet.js
//
// Verifying a phone number without leaving the invitation.
//
// ── Why this exists ────────────────────────────────────────────────────────
//
// The old prompt said "Verify your phone number. Go to Account Details?" and,
// if you said yes, navigated away from a finished invitation to a settings
// screen — where you would verify, and then have to find your way back and
// work out whether anything had been saved. Most people would not come back.
//
// The whole question is two fields long. Asking it here, finishing it here,
// and then publishing means the interruption costs a minute instead of a
// session. The publish continues by itself on success, so the host's last
// action is still the Publish button they already pressed.
//
// ── The server does the deciding ───────────────────────────────────────────
//
// `phone_send` and `phone_verify` in the auth-codes Edge Function hash the
// code, cap the attempts and rate-limit the sends, and that function is the
// only thing that can set `users.phone_verified`. Nothing here is trusted: a
// page that could mark itself verified would make the whole gate decorative.

import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiChevronDown } from 'react-icons/fi';
import { sendPhoneCode, verifyPhoneCode, sendEmailCode, verifyEmailCode } from '../../services/authCodes';
import PhoneCodePicker from '../PhoneCodePicker';
import { parseIdentifier } from '../../shared/authOptions';
import usePhoneCountry from '../../hooks/usePhoneCountry';
import useCountryNudge, { CountryCodeHint } from '../../hooks/useCountryNudge';
import '../../styles/PhoneVerifySheet.scss';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

/**
 * @param {'phone'|'email'} [props.kind]  what to verify - the publish gate asks
 *   for whichever the account is missing (shared/verificationGate.js)
 */
export default function PhoneVerifySheet({ reasonText, onVerified, onCancel, kind = 'phone' }) {
  const isEmail = kind === 'email';
  const [step, setStep] = useState('phone'); // 'phone' (the address step, either kind) | 'code'
  const [emailValue, setEmailValue] = useState('');
  // The visitor's own country (hooks/usePhoneCountry), not India for
  // everyone: +91 in front of an American number sent it to MSG91 as an
  // Indian one, and no SMS arrived. Blank ('') when it cannot be told - then
  // sending asks for it (useCountryNudge).
  const [detectedCountry] = usePhoneCountry();
  const [pickedDial, setPickedDial] = useState(null);
  const dialCode = pickedDial ?? (detectedCountry ? `+${detectedCountry.code}` : '');
  const countryNudge = useCountryNudge(dialCode || null);
const [showPicker, setShowPicker] = useState(false);
  const [phone, setPhone] = useState('');
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  const boxes = useRef([]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onCancel]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Without a trunk 0 ("098765 43210") or the code typed twice.
  const parsedPhone = parseIdentifier(phone, dialCode.replace(/\D/g, ''));
  const fullNumber = parsedPhone.kind === 'phone'
    ? `+${parsedPhone.phone}`
    : `${dialCode}${phone.replace(/\D/g, '').replace(/^0+/, '')}`;
const cleanEmail = emailValue.trim().toLowerCase();

  const send = async () => {
    // No country detected or picked: ask for it, rather than guess one.
    if (!isEmail && !countryNudge.check()) return;
    if (isEmail) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cleanEmail)) {
        setError('Enter a valid email address.');
        return;
      }
    } else {
      const national = phone.replace(/\D/g, '');
      // Loose on purpose: national numbering plans vary from 6 to 12 digits and
      // rejecting a real number is worse than letting the server say no.
      if (national.length < 6) {
        setError('That number looks too short.');
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      if (isEmail) await sendEmailCode(cleanEmail);
      else await sendPhoneCode(fullNumber);
      setStep('code');
      setCooldown(RESEND_SECONDS);
      setTimeout(() => boxes.current[0]?.focus(), 50);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const setDigit = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setError(null);
    if (digit && index < OTP_LENGTH - 1) boxes.current[index + 1]?.focus();
  };

  const onKeyDown = (index, e) => {
    // Backspace on an empty box steps back, which is what everyone expects
    // and almost no OTP field does.
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      boxes.current[index - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    // Phones offer the code from the SMS as one paste; typing it into six
    // separate boxes by hand is the thing people hate about this screen.
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setDigits(next);
    boxes.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const verify = async () => {
    const code = digits.join('');
    if (code.length !== OTP_LENGTH) {
      setError('Enter all six digits.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (isEmail) await verifyEmailCode(cleanEmail, code);
      else await verifyPhoneCode(fullNumber, code);
      // Straight on to publishing — the host already pressed that button.
      onVerified();
    } catch (err) {
      setError(err.message);
      setDigits(Array(OTP_LENGTH).fill(''));
      boxes.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pv-scrim" onClick={onCancel}>
      <div
        className="pv-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={isEmail ? 'Verify your email' : 'Verify your phone'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pv-head">
          <h2>{step === 'phone' ? (isEmail ? 'Verify your email' : 'Verify your phone') : 'Enter the code'}</h2>
          <button type="button" onClick={onCancel} aria-label="Cancel"><FiX /></button>
        </div>

        <div className="pv-body">
          {step === 'phone' ? (
            <>
              {/* The reason, from the shared gate, so the app says the same. */}
              <p className="pv-why">{reasonText}</p>

              {isEmail ? (
                <>
                  <label className="pv-label" htmlFor="pv-email">Email</label>
                  <input
                    id="pv-email"
                    className="pv-email"
                    type="email"
                    autoComplete="email"
                    value={emailValue}
                    placeholder="you@example.com"
                    onChange={(e) => { setEmailValue(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                    autoFocus
                  />
                </>
              ) : (
              <>
              <label className="pv-label" htmlFor="pv-phone">Phone number</label>
              <div className="pv-phone-row">
                <button
                  ref={countryNudge.ref}
                  type="button"
                  className={`pv-dial ${countryNudge.className}`}
                  onClick={() => setShowPicker(true)}
                  aria-label={dialCode ? 'Country code' : 'Pick your country code'}
                >
                  {dialCode || 'Code'}
                  <FiChevronDown />
                </button>
                <input
                  id="pv-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phone}
                  placeholder="98765 43210"
                  onChange={(e) => { setPhone(e.target.value); setError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                  autoFocus
                />
              </div>
              <CountryCodeHint nudge={countryNudge} />
              </>
              )}

              {error && <p className="pv-error">{error}</p>}

              <p className="pv-note">
                {isEmail
                  ? 'Only ever asked once. Your email is not shown on the invitation.'
                  : 'Only ever asked once. Your number is not shown on the invitation.'}
              </p>
            </>
          ) : (
            <>
              <p className="pv-why">
                We sent a six-digit code to <strong>{isEmail ? cleanEmail : fullNumber}</strong>.
              </p>

              <div className="pv-otp" onPaste={onPaste}>
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { boxes.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    // One-time-code lets iOS and Android offer the SMS code
                    // above the keyboard instead of making anyone read it.
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => setDigit(index, e.target.value)}
                    onKeyDown={(e) => onKeyDown(index, e)}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>

              {error && <p className="pv-error">{error}</p>}

              <button
                type="button"
                className="pv-link"
                disabled={cooldown > 0 || busy}
                onClick={send}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send it again'}
              </button>
            </>
          )}
        </div>

        <div className="pv-actions">
          <button type="button" className="pv-btn" onClick={onCancel}>
            {/* Not "Cancel": the invitation is not going anywhere, and saying
                so stops this reading like losing the lot. */}
            Not now
          </button>
          <button
            type="button"
            className="pv-btn pv-btn--primary"
            onClick={step === 'phone' ? send : verify}
            disabled={busy}
          >
            {busy
              ? (step === 'phone' ? 'Sending…' : 'Checking…')
              : (step === 'phone' ? 'Send code' : 'Verify & publish')}
          </button>
        </div>
      </div>

      {showPicker && (
        <PhoneCodePicker
          currentValue={dialCode}
          onSelect={(country) => { setPickedDial(country.code); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
